import { MatchingProfilePreviewCard } from "@/components/mentor-profile/matching-profile-preview-card";
import { PhoneFrame } from "@/components/mentor-profile/phone-frame";
import { useToast } from "@/components/ui/use-toast";
import { useAuth } from "@/components/auth/auth-context";
import {
  useApproveMentorProfileReview,
  useMentorProfileReviewById,
  useRequestMentorProfileChanges,
} from "@/lib/api/mentor-profile";
import { mentorProfileReviewRound } from "@/lib/mentor-profile/analytics";
import { changedSectionLabels } from "@/lib/mentor-profile/revision-change-detection";
import {
  PROFILE_SECTIONS,
  sectionSummary,
  type ReviewSection,
} from "@/lib/mentor-profile/profile-sections";
import { PostHogEvents, useTrackEvent, useTrackPage } from "@/lib/posthog-events";
import { cn } from "@/lib/utils";
import { Button } from "@somethings-ui/web";
import { Link, useNavigate } from "@tanstack/react-router";
import { ArrowLeft, Check, Clock, Loader2, Pencil, Plus } from "lucide-react";
import { useMemo, useState } from "react";

interface MentorProfileAdminReviewScreenProps {
  reviewId: string;
}

function statusLabel(status: string) {
  switch (status) {
    case "submitted":
    case "in_review":
      return "Clinical review · submitted for review";
    case "changes_requested":
      return "Clinical review · changes requested";
    case "approved":
      return "Clinical review · approved";
    default:
      return `Clinical review · ${status.replaceAll("_", " ")}`;
  }
}

export function MentorProfileAdminReviewScreen({
  reviewId,
}: MentorProfileAdminReviewScreenProps) {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { user } = useAuth();
  const { track } = useTrackEvent();
  const { data: review, isPending, isError } = useMentorProfileReviewById(
    reviewId,
  );
  const approve = useApproveMentorProfileReview();
  const requestChanges = useRequestMentorProfileChanges();
  const [notes, setNotes] = useState<Partial<Record<ReviewSection, string>>>(
    {},
  );
  const [requesting, setRequesting] = useState<Set<ReviewSection>>(new Set());
  const [error, setError] = useState<string | null>(null);
  const [showingLive, setShowingLive] = useState(false);

  useTrackPage(
    PostHogEvents.LEAD_MENTOR_REVIEW_OPENED,
    {
      mentor_id: review?.mentor_id,
      lead_mentor_id: user?.id,
    },
    { enabled: Boolean(review) },
  );

  const filledNotes = useMemo(
    () =>
      PROFILE_SECTIONS.map((section) => ({
        section: section.reviewSection,
        note: notes[section.reviewSection]?.trim() ?? "",
      })).filter((entry) => entry.note.length > 0),
    [notes],
  );
  const hasNotes = filledNotes.length > 0;
  const isBusy = approve.isPending || requestChanges.isPending;

  const mentorName =
    review?.draft.display_name ||
    [review?.mentor?.first_name, review?.mentor?.last_name]
      .filter(Boolean)
      .join(" ") ||
    review?.draft.first_name ||
    "Mentor";
  const noteRecipient =
    review?.draft.display_name ||
    review?.mentor?.first_name ||
    review?.draft.first_name ||
    "the mentor";

  const handleApprove = async () => {
    if (!review) return;
    setError(null);
    try {
      await approve.mutateAsync({ reviewId });
      track(PostHogEvents.MENTOR_PROFILE_APPROVED, {
        mentor_id: review.mentor_id,
        lead_mentor_id: user?.id,
        review_round: mentorProfileReviewRound(review.change_requests),
      });
      toast({
        title: `${mentorName}’s profile is approved`,
        description: "This profile is now discoverable by mentees",
      });
      void navigate({ to: "/users" });
    } catch {
      setError("Could not approve this profile. Please try again.");
    }
  };

  const handleReject = async () => {
    if (!review) return;
    setError(null);
    const changeCount = filledNotes.length;
    try {
      await requestChanges.mutateAsync({
        reviewId,
        changes: filledNotes,
      });
      track(PostHogEvents.MENTOR_PROFILE_REJECTED, {
        mentor_id: review.mentor_id,
        lead_mentor_id: user?.id,
        criteria_with_comments: filledNotes.map((n) => n.section),
        review_round: mentorProfileReviewRound(review.change_requests),
      });
      toast({
        title: `Sent ${changeCount} change${changeCount === 1 ? "" : "s"} to ${mentorName}`,
        description: "Your profile review was sent for revisions",
      });
      void navigate({ to: "/users" });
    } catch {
      setError("Could not send change requests. Please try again.");
    }
  };

  const clearAllChanges = () => {
    setNotes({});
    setRequesting(new Set());
  };

  const openRequest = (section: ReviewSection) => {
    setRequesting((prev) => new Set(prev).add(section));
  };

  const setSectionNote = (section: ReviewSection, note: string) => {
    const prevNote = notes[section] ?? "";
    if (!prevNote.trim() && note.trim() && review) {
      track(PostHogEvents.LEAD_MENTOR_CRITERION_COMMENT_ADDED, {
        mentor_id: review.mentor_id,
        lead_mentor_id: user?.id,
        criterion: section,
      });
    }
    setNotes((prev) => ({ ...prev, [section]: note }));
  };

  const closeRequest = (section: ReviewSection) => {
    setRequesting((prev) => {
      const next = new Set(prev);
      next.delete(section);
      return next;
    });
    setNotes((prev) => {
      const next = { ...prev };
      delete next[section];
      return next;
    });
  };

  if (isPending) {
    return (
      <div className="flex min-h-[50vh] items-center justify-center gap-2 text-text-secondary">
        <Loader2 className="size-5 animate-spin" />
        Loading review…
      </div>
    );
  }

  if (isError || !review) {
    return (
      <div className="m-6 rounded-xl border border-states-error/30 bg-states-errorHighlight p-4 text-sm text-states-error">
        Could not load this profile review.
      </div>
    );
  }

  let previewDraft = review.draft;
  if (showingLive && review.published_snapshot) {
    previewDraft = review.published_snapshot;
  }

  return (
    <div className="m-6 pb-24">
      <div className="grid gap-10 lg:grid-cols-[minmax(0,1fr)_360px] lg:items-start">
        <div className="min-w-0">
          <Link
            to="/users"
            className="inline-flex h-11 items-center gap-2 font-heading text-sm font-bold tracking-[-0.14px] text-text-primary"
          >
            <ArrowLeft className="size-[18px]" />
            Back to Users
          </Link>

          <p className="mt-2.5 font-body text-xs italic leading-[1.4] text-text-secondary">
            {statusLabel(review.status)}
          </p>
          <h1 className="mt-1 font-heading text-2xl font-bold leading-[1.2] tracking-[-0.48px] text-text-primary">
            Review {mentorName}&apos;s profile
          </h1>
          <p className="mt-2 max-w-xl font-body text-sm leading-[1.4] text-text-secondary">
            Check what teens will see on the right. Flag anything that needs a
            change — each note goes to {noteRecipient} tied to that section.
          </p>

          {review.published_snapshot ? (
            <ChangedSectionsBanner
              labels={changedSectionLabels(
                review.draft,
                review.published_snapshot,
              )}
            />
          ) : null}

          <div className="mt-6 flex flex-col gap-2">
            {PROFILE_SECTIONS.map((section) => {
              const summary = sectionSummary(
                review.draft,
                section.reviewSection,
              );
              const isOpen =
                requesting.has(section.reviewSection) ||
                (notes[section.reviewSection]?.trim().length ?? 0) > 0;

              return (
                <div
                  key={section.id}
                  className={cn(
                    "rounded-2xl border bg-surfaces-z0 p-4",
                    isOpen ? "border-text-primary" : "border-surfaces-z5",
                  )}
                >
                  <div className="flex items-start gap-4">
                    <div className="min-w-0 flex-1">
                      <p className="font-heading text-base font-bold leading-6 text-text-primary">
                        {section.label}
                      </p>
                      <div className="mt-0.5 flex items-start gap-1">
                        {summary.checked ? (
                          <Check
                            className="mt-0.5 size-[18px] shrink-0 text-states-success"
                            strokeWidth={2.5}
                          />
                        ) : null}
                        <p className="font-body text-sm leading-5 text-text-secondary">
                          {summary.text}
                        </p>
                      </div>
                    </div>

                    {isOpen ? (
                      <Button
                        size="sm"
                        variant="quaternary"
                        fullWidth={false}
                        leadingIcon={<Pencil className="size-[18px]" />}
                        onPress={() => closeRequest(section.reviewSection)}
                      >
                        Change requested
                      </Button>
                    ) : (
                      <Button
                        size="sm"
                        variant="tertiary"
                        fullWidth={false}
                        leadingIcon={<Plus className="size-5" strokeWidth={1.75} />}
                        onPress={() => openRequest(section.reviewSection)}
                      >
                        Request a change
                      </Button>
                    )}
                  </div>

                  {isOpen ? (
                    <div className="mt-3">
                      <p className="font-mono text-[10px] font-medium leading-3 text-text-secondary">
                        Note to User
                      </p>
                      <textarea
                        value={notes[section.reviewSection] ?? ""}
                        onChange={(e) =>
                          setSectionNote(section.reviewSection, e.target.value)
                        }
                        rows={3}
                        placeholder="What should they change in this section?"
                        className="mt-1.5 min-h-24 w-full resize-y rounded-lg border border-surfaces-z5 bg-surfaces-z0 px-4 py-3 font-body text-base leading-[1.4] text-text-primary outline-none placeholder:text-text-placeholder focus:border-text-primary"
                      />
                    </div>
                  ) : null}
                </div>
              );
            })}
          </div>

          {error ? (
            <p className="mt-4 font-body text-sm text-states-error">{error}</p>
          ) : null}

          <div className="mt-6 flex flex-wrap items-center justify-end gap-3">
            {hasNotes ? (
              <>
                <Button
                  size="lg"
                  variant="secondary"
                  fullWidth={false}
                  disabled={isBusy}
                  onPress={clearAllChanges}
                >
                  Clear all changes
                </Button>
                <Button
                  size="lg"
                  fullWidth={false}
                  className="w-full max-w-sm"
                  disabled={isBusy}
                  loading={requestChanges.isPending}
                  onPress={() => void handleReject()}
                >
                  Reject with comments
                </Button>
              </>
            ) : (
              <>
                <Button size="lg" variant="secondary" fullWidth={false} disabled>
                  Reject with comments
                </Button>
                <Button
                  size="lg"
                  fullWidth={false}
                  className="w-full max-w-sm"
                  disabled={isBusy}
                  loading={approve.isPending}
                  leadingIcon={
                    approve.isPending ? undefined : (
                      <Check className="size-[18px]" strokeWidth={2.5} />
                    )
                  }
                  onPress={() => void handleApprove()}
                >
                  Approve and publish
                </Button>
              </>
            )}
          </div>
        </div>

        <div className="flex flex-col items-center lg:sticky lg:top-6">
          {review.published_snapshot ? (
            <PreviewToggle
              showingLive={showingLive}
              onChange={setShowingLive}
            />
          ) : null}
          <PhoneFrame>
            <MatchingProfilePreviewCard draft={previewDraft} />
          </PhoneFrame>
        </div>
      </div>
    </div>
  );
}

function ChangedSectionsBanner({ labels }: { labels: string[] }) {
  if (labels.length === 0) return null;

  return (
    <div className="mt-6 flex items-center gap-2 rounded-2xl border border-[#f3e0a4] bg-[#fff6df] px-4 py-3">
      <Clock
        className="size-4 shrink-0 text-[#1f1e1d]"
        strokeWidth={1.75}
        aria-hidden
      />
      <p className="font-body text-sm leading-5 text-[#1f1e1d]">
        Changes made to {labels.join(", ")}
      </p>
    </div>
  );
}

function PreviewToggle({
  showingLive,
  onChange,
}: {
  showingLive: boolean;
  onChange: (showingLive: boolean) => void;
}) {
  return (
    <div
      role="group"
      aria-label="Profile preview"
      className="mb-3 flex h-9 w-[360px] items-center rounded-full bg-[#f6f4ef] p-1"
    >
      <button
        type="button"
        aria-pressed={!showingLive}
        className={cn(
          "flex h-full flex-1 items-center justify-center rounded-full font-body text-sm leading-4",
          showingLive
            ? "text-[#58534b]"
            : "border border-[#dfddd8] bg-white font-medium text-black shadow-[0px_3px_6px_rgba(0,0,0,0.13)]",
        )}
        onClick={() => onChange(false)}
      >
        Proposed Changes
      </button>
      <button
        type="button"
        aria-pressed={showingLive}
        className={cn(
          "flex h-full flex-1 items-center justify-center rounded-full font-body text-sm leading-4",
          showingLive
            ? "border border-[#dfddd8] bg-white font-medium text-black shadow-[0px_3px_6px_rgba(0,0,0,0.13)]"
            : "text-[#58534b]",
        )}
        onClick={() => onChange(true)}
      >
        Current Preview
      </button>
    </div>
  );
}
